import React, { useCallback, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocation, useNavigate, useParams } from 'react-router'

// evertel
import { useContainer, useService } from '@evertel/di'
import { DocumentViewController } from '@evertel/document'
import { DocumentShareModal } from './acl/DocumentShareModal'
import { PageBody, Breadcrumb, PhotoGallery } from '@evertel/web/ui'
import { SessionService } from '@evertel/session/SessionService'
import { DocumentFullView } from './DocumentFullView'
import { PDFViewer } from '@react-pdf/renderer'
import { DocumentPDF } from './DocumentPDF'
import { useDocumentTitleSuffix } from '@evertel/web/core'


const DocumentView: React.FC = observer(() => {

    const { id: docStringId } = useParams()
    const documentId = docStringId ? parseInt(docStringId) : null

    const sessionService = useService(SessionService, [])
    const documentViewController = useService(DocumentViewController, [])

    const [showShareModal, setShowShareModal] = useState(false)
    const [showInlinePdf, setShowInlinePdf] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const navigate = useNavigate()
    const location = useLocation()
    const { setTitleSuffix } = useDocumentTitleSuffix()

    // Set document title suffix
    useEffect(() => {
        if (documentId) {
            setTitleSuffix(`#${documentId}`)
        }
    }, [documentId, setTitleSuffix])

    useEffect(() => {
        (async () => {
            if (!documentId) return

            setIsLoading(true)

            try {
                await Promise.all([
                    documentViewController.load( documentId )
                ])

            } catch (error) {
                console.error(error.message)
            }

            setIsLoading(false)
        })()

    }, [documentId, sessionService.currentUserId])

    const onEditDoc = (documentId) => {
        //pass along current location to determine if clicking back is ok from the edit doc view
        navigate(`/everdocs/edit/${documentId}`, {
            state: {
                from: location.pathname + location.search
            }
        })
    }

    const handleTogglePdfPreview = () => {
        if (!showInlinePdf) {
            // Track when showing PDF (same as download tracking)
            documentViewController.trackPdfDownload(documentId).catch(error => {
                console.error('Error tracking PDF preview:', error)
            })
        }
        setShowInlinePdf(!showInlinePdf)
    }

    const container = useContainer()

    if (!documentViewController.document) return null

    const breadcrumbs = [
        { label: 'EverDocs', onClick: () => navigate('/everdocs') },
        { label: `Viewing (${documentViewController.name})`, active: true }
    ]

    return (
        <PageBody>
            <Breadcrumb
                items={breadcrumbs}
            />
            <PhotoGallery>
                <DocumentFullView
                    documentId={documentId}
                    onEdit={onEditDoc}
                    onShare={() => setShowShareModal(true)}
                />
            </PhotoGallery>

            <button className="btn btn-primary btn-sm d-block" style={{margin: '10px auto'}} onClick={handleTogglePdfPreview}>
                {showInlinePdf ? 'Hide' : 'Show'} PDF Preview
            </button>

            {showInlinePdf && (
                <PDFViewer width="100%" height={800} showToolbar>
                    <DocumentPDF
                        user={sessionService.currentUserId}
                        document={documentViewController.document}
                        schema={documentViewController.documentSchema}
                        value={documentViewController.document.data}
                        container={container}
                    />
                </PDFViewer>
            )}

            <DocumentShareModal
                isVisible={showShareModal}
                onClose={() => setShowShareModal(false)}
                documentId={documentId}
                currentUserId={sessionService.currentUserId}
                documentName={documentViewController.name}
            />
        </PageBody>
    )
})

export {
    DocumentView
}
